/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/camelcase */
import {
  Checkbox,
  Chip,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { Alert, AlertTitle } from '@material-ui/lab';
import Button from 'components/Button';
import { DialogAlertProps } from 'components/DialogAlert';
import Loading from 'components/Loading';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import {
  ListObjectCompanyDataProps as ListObjectProps,
  ObjectCompanyProps as ObjectProps,
} from 'modules/permission/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState, { ListDataProps } from 'store/interfaces';
import { getCompanyName } from 'utils/tsHelpers';
import { useTableFilters } from 'hooks/useTableFilters';
import { initialQuery, mapAllSelected } from '..';
import TemplateGroups from '../../Template';
import styles from '../../Template/styles';
import ActionButtons from '../ActionButtons';
import ListAvailable from './ListAvailable';
import ListPermissions from './ListPermissions';
import { handleCheck } from '../utils/utils';
import { handleObjectsSave } from '../utils/objectsUtils';
import { handlePermissionsSave } from '../utils/permissionsUtils';

const useStyles = makeStyles(styles);

export const ENTITY_NAME = 'company';
export const ENTITY_INDEX = 0;

interface TemplateProps {
  title: string;
  subtitle: React.ReactNode;
  slug: 'COMPANIES' | 'PARTNERS' | 'ENVIRONMENTS' | 'CLIENTS' | 'OPERATORS';
  tabs: {
    items: any[];
    defaultValue: number;
    onChange: (value: number) => void;
  };
}

interface Props {
  id: string;
  hidden?: boolean;
  template: TemplateProps;
  permissions: any;
  items: {
    loading: boolean;
    selected: ListObjectProps;
    available: ListObjectProps;
  };
  allItemsSelected: boolean;
  isLoading: boolean;
  onLoad: (entity: string, query?: typeof initialQuery) => void;
  onPrevStep?: () => void;
  onNextStep?: () => void;
  onCancel?: () => void;
  onRemove: (entity: string, objects: string[]) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  updateObjects: (
    entity: string,
    objects: string[],
    allSelected?: boolean
  ) => void;
  updatePermissions: (entity: string, permissions: string[]) => void;
  editGroup: () => void;
}

const Content = ({
  id,
  hidden,
  template,
  items,
  allItemsSelected,
  isLoading,
  onLoad,
  onPrevStep,
  onNextStep,
  onCancel,
  onRemove,
  modalOpen,
  modalClose,
  updateObjects,
  updatePermissions,
  editGroup,
  permissions,
}: Props) => {
  const classes = useStyles();
  const {
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
    setQuery,
  } = useTableFilters(initialQuery);

  const [checked, setChecked] = React.useState<string[]>([]);
  const [alert] = React.useState<DialogAlertProps | null>(null);
  const [data, setData] = React.useState<ListDataProps | null>(null);
  const [permissionItems, setPermissionItems] = React.useState<string[]>([]);
  const [pending, setPending] = React.useState<{
    objects: ObjectProps[];
    permissions: string[];
  }>({
    objects: [],
    permissions: [],
  });
  const [allSelected, setAllSelected] = React.useState(allItemsSelected);

  const currentTab = template.tabs.items[ENTITY_INDEX];

  const handleSave = () => {
    handleObjectsSave(
      pending.objects,
      data?.results,
      allItemsSelected,
      updateObjects,
      currentTab.entity,
      allSelected
    );
    handlePermissionsSave(
      pending.permissions,
      permissionItems,
      currentTab.entity,
      updatePermissions
    );
    setPending({ objects: [], permissions: [] });
  };

  const handleAdd = () => {
    modalOpen({
      title: i18n.t('GROUPS_PAGE.ADD_COMPANIES_KEY'),
      subtitle: i18n.t('GROUPS_PAGE.ADD_COMPANIES_SUBTITLE_KEY'),
      content: (
        <ListAvailable
          id={id}
          pending={pending.objects}
          allSelected={allSelected}
          onCancel={modalClose}
          onConfirm={(objects, all) => {
            modalClose();
            setAllSelected(all);
            setPending({ ...pending, objects });
          }}
        />
      ),
      maxWidth: 'md',
      fullWidth: true,
      noBorder: true,
    });
  };

  const handleRemove = (ids: string[]) => {
    const item = data?.results.find(({ uuid }) => uuid === ids[0]);
    const { name } = item;
    let titleKey = 'GROUPS_PAGE.WANT_REMOVE_SELECTED_COMPANIES_KEY';
    let contentKey = 'GROUPS_PAGE.ACTION_IS_IRREVERSIBLE_KEY';

    if (ids.length === 1) {
      titleKey = 'GROUPS_PAGE.WANT_REMOVE_COMPANY_KEY';
      contentKey = 'GROUPS_PAGE.REMOVE_WARNING_MSG_KEY';
    }

    modalOpen({
      title: i18n.t(titleKey),
      content: i18n.t(contentKey, { name }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              onRemove(currentTab.entity, ids);
              setChecked(prev => prev.filter(uuid => !ids.includes(uuid)));
              modalClose();
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  React.useEffect(() => {
    if (!items?.selected) return;

    setData(items.selected);

    currentTab.count = allItemsSelected ? 'ALL_KEY' : items.selected.count;
  }, [items, allItemsSelected]);

  React.useEffect(() => {
    setAllSelected(allItemsSelected);
    if (allItemsSelected) currentTab.count = 'ALL_KEY';
  }, [allItemsSelected]);

  React.useEffect(() => {
    if (!hidden && !allSelected) {
      setQuery(initialQuery);
    }
  }, [hidden]);

  React.useEffect(() => {
    if (!hidden && !allSelected) {
      onLoad(currentTab.entity, query);
    }
  }, [query]);

  if (!data) {
    return <Loading />;
  }

  return !hidden ? (
    <React.Fragment>
      <TemplateGroups
        {...template}
        contentTitle={i18n.t('GROUPS_PAGE.LIST_COMPANIES_TITLE_KEY')}
        contentSubtitle={i18n.t('GROUPS_PAGE.LIST_COMPANIES_SUBTITLE_KEY')}
        alert={alert}
        disableArea={permissions[ENTITY_NAME]?.loading}
        generalActions={[
          {
            label: i18n.t('ADD_COMPANY_KEY'),
            onClick: handleAdd,
          },
          {
            label: i18n.t('REMOVE_SELECTED_COMPANIES_KEY'),
            onClick: () => handleRemove(checked),
            disabled: checked.length === 0,
          },
          {
            label: i18n.t('EDIT_INFO_KEY'),
            onClick: editGroup,
          },
        ]}
        onSearch={handleSearch}
        selectAll={{
          value: allSelected,
          label: i18n.t('SELECT_ALL_COMPANIES_KEY'),
          onChange: event => {
            setAllSelected(event.target.checked);
            setPending({ ...pending, objects: [] });
          },
        }}
        isLoading={items?.loading || isLoading}
      >
        {pending.objects.length > 0 && (
          <Grid container xs={12} className={classes.pendingList}>
            <Alert severity="warning">
              <AlertTitle>{i18n.t('ITEMS_TO_ADD_KEY')}:</AlertTitle>
              {pending.objects.map(({ uuid, name }) => (
                <Chip
                  key={uuid}
                  label={name}
                  onDelete={() => {
                    setPending({
                      ...pending,
                      objects: pending.objects.filter(
                        item => uuid !== item?.uuid
                      ),
                    });
                  }}
                  variant="outlined"
                />
              ))}
            </Alert>
          </Grid>
        )}
        <TableItems
          overrideRowsCount={10}
          header={
            <>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  onChange={e => handleCheck.toggleAll(e, data, setChecked)}
                />
              </TableCell>
              <TableCell order handleOrder={() => handleOrder('company_name')}>
                {i18n.t('COMPANY_NAME_KEY')}
              </TableCell>
              <TableCell order handleOrder={() => handleOrder('trading_name')}>
                {i18n.t('SINGLE_CORPORATE_NAME_KEY')}
              </TableCell>
              <TableCell>{i18n.t('ZOHO_ID_KEY')}</TableCell>
              <TableCell centerTitle>{i18n.t('ACTIONS_KEY')}</TableCell>
            </>
          }
          renderItems={(item: ObjectProps) => (
            <TableRow key={item?.uuid}>
              <TableCell className={classes.checkboxCell} style={{ width: 20 }}>
                <Checkbox
                  onChange={() => handleCheck.toggle(item?.uuid, setChecked)}
                  checked={handleCheck.exists(item?.uuid, checked)}
                />
              </TableCell>
              <TableCell>{getCompanyName(item)}</TableCell>
              <TableCell>{item?.trading_name}</TableCell>
              <TableCell>{item?.zoho_id}</TableCell>
              <TableCell className={classes.deleteIcon}>
                <IconButton onClick={() => handleRemove([item?.uuid])}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          )}
          addButton={
            <Button
              variant="outlined"
              onClick={handleAdd}
              disabled={allItemsSelected}
            >
              {i18n.t('ADD_COMPANY_KEY')}
            </Button>
          }
          data={data}
          isLoading={items?.loading && data.count === 0}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={
            <>
              <Typography variant="body1" className={classes.notFountText}>
                {i18n.t('NO_ADDED_COMPANY_KEY')}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleAdd}
                disabled={allItemsSelected}
              >
                {i18n.t('ADD_COMPANY_KEY')}
              </Button>
            </>
          }
          colCount={6}
        />
      </TemplateGroups>

      <ListPermissions
        title={i18n.t('PERMISSIONS_KEY')}
        subtitle={i18n.t('GROUPS_PAGE.LIST_COMPANY_PERMISSIONS_KEY')}
        pending={pending.permissions}
        setPending={values => setPending({ ...pending, permissions: values })}
        updatePermissions={(values: string[]) => setPermissionItems(values)}
      />

      <ActionButtons
        disableSave={
          pending.objects.length === 0 &&
          pending.permissions.length === 0 &&
          allSelected === allItemsSelected
        }
        onCancel={onCancel}
        onSave={handleSave}
        onPrevStep={onPrevStep}
        onNextStep={onNextStep}
      />
    </React.Fragment>
  ) : null;
};

const mapStateToProps = ({
  permission: { objects, roleInfo, permissions, isLoading },
}: ApplicationState) => ({
  items: objects[ENTITY_NAME],
  permissions,
  allItemsSelected: roleInfo[mapAllSelected[ENTITY_NAME]],
  isLoading,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
})(Content);
